import React, { useContext, useEffect, useState } from 'react';
import FilerobotImageEditor, { TABS, TOOLS } from 'react-filerobot-image-editor';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import { useDispatch } from 'react-redux';

import { imgApi } from '../../../api/img-api';
import { setShow } from '../../../redux/slices/chatbotSlice';
import { useModal } from '../../../shared/hooks';
import { HW, ICL, TaskKeysType } from '../../../shared/types';
import { EditorContext } from '../EditorProvider';

interface Props {
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | string[] | boolean) => void;
  icl: ICL | HW | null;
}

export default function EditorCanvas({ icl, handleChangeTaskByKey }: Props) {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const { setImageBase64, setImageForEdit, imageForEdit, targetTaskId, editedImageArray } = useContext(EditorContext);
  const { isOpen, close, open } = useModal();

  const [isImgEditorShown, setIsImgEditorShown] = useState(false);

  const closeImgEditor = () => {
    setIsImgEditorShown(false);
    setImageForEdit('');
    close();
    dispatch(setShow(true));
  };

  useEffect(() => {
    if (imageForEdit) {
      open();
      setIsImgEditorShown(true);
      dispatch(setShow(false));
    }
  }, [imageForEdit]);

  async function uploadBase64ImageToStorage(base64String: string) {
    const base64Regex = /^data:(image\/\w+);base64,/;
    if (!base64Regex.test(base64String)) {
      throw new Error('Invalid base64 string format. Please ensure it starts with "data:image/..."');
    }
    const decodedString = atob(base64String.split(',')[1]);
    const mimeType = base64String.split(',')[0].split(':')[1].split(';')[0];
    const arrayBuffer = new ArrayBuffer(decodedString.length);
    const uint8Array = new Uint8Array(arrayBuffer);
    for (let i = 0; i < decodedString.length; i++) {
      uint8Array[i] = decodedString.charCodeAt(i);
    }
    const blob = new Blob([uint8Array], { type: mimeType });
    const file = new File([blob], 'edited.jpg', { type: mimeType });

    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    const existingImageIndex = editedImageArray.indexOf(imageForEdit);
    if (existingImageIndex !== -1) {
      editedImageArray.splice(existingImageIndex, 1);
    }

    try {
      const gottenImageUrlArr = await imgApi.addImg(
        file,
        icl?.id,
        targetTaskId,
        editedImageArray,
        'editedAnswerPhotos',
      );

      handleChangeTaskByKey(targetTaskId, 'editedAnswerPhotos', gottenImageUrlArr);

      closeImgEditor();
      toast.success(t('Successfully added!'));
    } catch (error) {
      console.log(error);
      toast.error(t('Something went wrong. Try again!'));
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeImgEditor}
      className="relative bottom-auto mx-auto h-[90%] w-full translate-y-[10vh] rounded-t-[24px] bg-white p-[10px] lg:w-[80vw]"
      overlayClassName="w-full fixed top-0 left-0 rigth-0 bottom-0 bg-[#0d0d0ccc]"
      closeTimeoutMS={300}
    >
      {isImgEditorShown && (
        <FilerobotImageEditor
          source={imageForEdit}
          onSave={(editedImageObject, designState) => {
            setImageBase64(editedImageObject.imageBase64);

            if (editedImageObject.imageBase64) {
              uploadBase64ImageToStorage(editedImageObject.imageBase64);
            }
          }}
          onClose={closeImgEditor}
          annotationsCommon={{
            fill: '#ff0000',
          }}
          Text={{ text: '' }}
          Rotate={{ angle: 90, componentType: 'slider' }}
          Crop={{
            presetsItems: [
              {
                titleKey: 'classicTv',
                descriptionKey: '4:3',
                ratio: 4 / 3,
              },
              {
                titleKey: 'cinemascope',
                descriptionKey: '21:9',
                ratio: 21 / 9,
              },
            ],
          }}
          tabsIds={[TABS.ADJUST, TABS.ANNOTATE]}
          defaultTabId={TABS.ANNOTATE}
          defaultToolId={TOOLS.TEXT}
          translations={{
            name: t('nameEditor'),
            save: t('saveEditor'),
            saveAs: t('saveAs'),
            back: t('back'),
            loading: t('loadingEditor'),
            resetOperations: t('resetOperations'),
            changesLoseWarningHint: t('changesLoseWarningHint'),
            discardChangesWarningHint: t('discardChangesWarningHint'),
            cancel: t('cancelEditor'),
            apply: t('apply'),
            warning: t('warning'),
            confirm: t('confirm'),
            discardChanges: t('discardChanges'),
            undoTitle: t('undoTitle'),
            redoTitle: t('redoTitle'),
            showImageTitle: t('showImageTitle'),
            zoomInTitle: t('zoomInTitle'),
            zoomOutTitle: t('zoomOutTitle'),
            toggleZoomMenuTitle: t('toggleZoomMenuTitle'),
            adjustTab: t('adjustTab'),
            finetuneTab: t('finetuneTab'),
            filtersTab: t('filtersTab'),
            watermarkTab: t('watermarkTab'),
            annotateTabLabel: t('annotateTabLabel'),
            resize: t('resize'),
            resizeTab: t('resizeTab'),
            imageName: t('imageName'),
            invalidImageError: t('invalidImageError'),
            uploadImageError: t('uploadImageError'),
            areNotImages: t('areNotImages'),
            isNotImage: t('isNotImage'),
            toBeUploaded: t('toBeUploaded'),
            cropTool: t('cropTool'),
            original: t('original'),
            custom: t('custom'),
            square: t('square'),
            landscape: t('landscape'),
            portrait: t('portrait'),
            ellipse: t('ellipse'),
            classicTv: t('classicTv'),
            cinemascope: t('cinemascope'),
            arrowTool: t('arrowTool'),
            blurTool: t('blurTool'),
            brightnessTool: t('brightnessTool'),
            contrastTool: t('contrastTool'),
            ellipseTool: t('ellipseTool'),
            unFlipX: t('unFlipX'),
            flipX: t('flipX'),
            unFlipY: t('unFlipY'),
            flipY: t('flipY'),
            hsvTool: t('hsvTool'),
            hue: t('hue'),
            brightness: t('brightness'),
            saturation: t('saturation'),
            value: t('value'),
            imageTool: t('imageTool'),
            importing: t('importing'),
            addImage: t('addImage'),
            uploadImage: t('uploadImage'),
            fromGallery: t('fromGallery'),
            lineTool: t('lineTool'),
            penTool: t('penTool'),
            polygonTool: t('polygonTool'),
            sides: t('sides'),
            rectangleTool: t('rectangleTool'),
            cornerRadius: t('cornerRadius'),
            resizeWidthTitle: t('resizeWidthTitle'),
            resizeHeightTitle: t('resizeHeightTitle'),
            toggleRatioLockTitle: t('toggleRatioLockTitle'),
            resetSize: t('resetSize'),
            rotateTool: t('rotateTool'),
            textTool: t('textTool'),
            textSpacings: t('textSpacings'),
            textAlignment: t('textAlignment'),
            fontFamily: t('fontFamily'),
            size: t('sizeEditor'),
            letterSpacing: t('letterSpacing'),
            lineHeight: t('lineHeight'),
            warmthTool: t('warmthTool'),
            addWatermark: t('addWatermark'),
            addTextWatermark: t('addTextWatermark'),
            addWatermarkTitle: t('addWatermarkTitle'),
            uploadWatermark: t('uploadWatermark'),
            addWatermarkAsText: t('addWatermarkAsText'),
            padding: t('padding'),
            paddings: t('paddings'),
            shadow: t('shadow'),
            horizontal: t('horizontal'),
            vertical: t('vertical'),
            blur: t('blur'),
            opacity: t('opacity'),
            transparency: t('transparency'),
            position: t('position'),
            stroke: t('stroke'),
            saveAsModalTitle: t('saveAsModalTitle'),
            extension: t('extension'),
            format: t('format'),
            nameIsRequired: t('nameIsRequired'),
            quality: t('quality'),
            imageDimensionsHoverTitle: t('imageDimensionsHoverTitle'),
            cropSizeLowerThanResizedWarning: t('cropSizeLowerThanResizedWarning'),
            actualSize: t('actualSize'),
            fitSize: t('fitSize'),
            addImageTitle: t('addImageTitle'),
            mutualizedFailedToLoadImg: t('mutualizedFailedToLoadImg'),
            tabsMenu: t('tabsMenu'),
            download: t('downloadEditor'),
            width: t('width'),
            height: t('height'),
            plus: t('plus'),
            cropItemNoEffect: t('cropItemNoEffect'),
          }}
          closeAfterSave
          defaultSavedImageName={'edited'}
        />
      )}
    </Modal>
  );
}
